import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import type {
  AvailableReward,
  EscalationData,
  MockOrder,
  MockOrderScenario,
  RecoveryConfig,
  ResolutionData,
} from 'api/recoveryAi.types'
import Spinner from 'components/Spinner'
import palette from 'constants/palette'
import type { ChatMessage } from 'hooks/websocket/recoveryChatSource'
import { buildTranslate } from 'locales'
import React, { useMemo } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import type { RowKey } from '../components/AgentTuningConfigModals'
import AgentSettingsSummary from './AgentSettingsSummary'
import ChatPanel from './ChatPanel'
import DecisionLogPanel from './DecisionLogPanel'
import { getFreeChatOrder } from './freeChatOrder'
import offerFromResolution from './offerFromResolution'
import OrderDetailsCard from './OrderDetailsCard'
import PhoneFrame, { PHONE_FIXED_HEIGHT_PX } from './PhoneFrame'
import ReasoningChecklist from './ReasoningChecklist'
import SandboxPromptAccordion from './SandboxPromptAccordion'
import SandboxRewardCard from './SandboxRewardCard'
import ScenarioDetailPanel from './ScenarioDetailPanel'
import type { ScenarioReasoningStep } from './simulatorTypes'
import { TOUR_ANCHORS } from './tourAnchors'
import { useMerchantBrand } from './useMerchantBrand'

const t = buildTranslate('recovery_ai.sandbox')

// ENG-971: the pager chip is a capsule — a radius well past half its height.
const CHIP_RADIUS_PX = 999

// Floor for the settings-applying wait state in the flat 'panel' presentation,
// which has no device to inherit a height from. Matches ChatPanel's own non-bare
// `minHeight` so swapping the conversation out does not collapse the Agent Tuning
// column and shift everything below it. The phone presentation ignores this — the
// device is already pinned to PHONE_FIXED_HEIGHT_PX.
const APPLYING_PANEL_MIN_HEIGHT_PX = 500

// Figma puts the phone and the settings column on EQUAL tracks — 439px each with a
// 28px gutter, so 906px total — on the wizard AND the Agent Tuning tab. The two
// surfaces are meant to mirror each other, so both take these numbers.
//   context: the wizard used to run '360px 1fr' at a 960px cap, which resolved its
//   right column to 584px — 33% wider than the design. That is what made the
//   reasoning prose run long and the settings rows look stretched, and it is why
//   every type/padding value in this redesign was measured against a 439px column.
const PHONE_GRID_MAX_WIDTH_PX = 906
const PHONE_GRID_GAP_PX = 28
// Every other (non-phone) layout keeps the original gutter.
const DEFAULT_GRID_GAP_PX = 16

// The centre header's first row: the pager chip while guided, the reset link once
// free. Both phases render exactly one, so this style is what makes the two the
// SAME height and stops the phone shifting on the transition.
//
//   context: a FIXED height, not a minHeight. A floor cannot equalise two boxes —
//   whichever content is naturally taller simply wins and the floor does nothing.
//   That was the bug in the first attempt at this: the chip (12px text + 2px
//   vertical padding, ~19px) got floored to 20px, but the reset link is a baseui
//   Button carrying its own line-height, so it stayed taller and the phone kept
//   twitching a few pixels on the transition. Pinning the height makes the two
//   rows identical whatever their contents resolve to.
//   context: paired with a lineHeight in TEXT_LINK_OVERRIDES so the Button cannot
//   exceed the box and overflow it.
const HEADER_ROW_HEIGHT_PX = 24
const headerRowStyle = {
  height: `${HEADER_ROW_HEIGHT_PX}px`,
  marginBottom: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

// Styling for the centre header's reset text link. NOTE: uikit's Button
// shallow-spreads caller overrides over its own kind-derived BaseButton entry, so
// a PARTIAL override silently discards `kind="minimal"` and yields baseui's filled
// default — this object has to stay complete, transparent background included.
const TEXT_LINK_OVERRIDES = {
  BaseButton: {
    style: {
      backgroundColor: 'transparent',
      color: palette.belizeHole,
      fontSize: '14px',
      // Matches HEADER_ROW_HEIGHT_PX so the reset link cannot grow its row past
      // the pager chip's — baseui supplies its own line-height otherwise, which is
      // what made the phone shift on the guided → free transition.
      lineHeight: `${HEADER_ROW_HEIGHT_PX}px`,
      paddingTop: '0',
      paddingBottom: '0',
      paddingLeft: '0',
      paddingRight: '0',
      ':hover:enabled': {
        backgroundColor: 'transparent',
        color: palette.belizeHole,
      },
      ':active:enabled': {
        backgroundColor: 'transparent',
      },
    },
  },
}

type TerminalOutcome = 'escalation' | 'resolution' | 'none'

// Escalation beats resolution when both payloads are present — see the call site
// for why the reassuring direction is the dangerous one.
const resolveTerminalOutcome = (
  resolution: ResolutionData | null,
  escalation: EscalationData | null
): TerminalOutcome => {
  if (escalation) return 'escalation'
  if (resolution) return 'resolution'
  return 'none'
}

/**
 * Pure presentational simulator core, extracted from the original in-tab sandbox
 * shell (AI-95). It owns NO data fetching and NO `useDispatch` — every value it
 * renders arrives as an already-resolved prop. This is the single engine reused
 * by the standalone shell and the wizard-embedded shell, so the chat /
 * scenario-detail / decision-log layout lives in exactly one place.
 */
type Props = {
  messages: ChatMessage[]
  currentStreamingMessage: string | null
  awaitingResponse: boolean
  isToolRunning: boolean
  onSendMessage: (text: string) => void
  assistantName?: string | null
  agentName?: string | null
  /**
   * The brand-voiced opening line (nexus canned responses, `type: greeting`),
   * forwarded straight to ChatPanel. The shells pass useSimulatorData()'s
   * `cannedGreeting`; ChatPanel falls back to its bundled template while it's
   * loading/unavailable — this engine stays a pure presenter either way.
   */
  cannedGreeting?: string
  /**
   * A settings save is still being applied, so nothing on the preview can be
   * trusted yet — clear the conversation off the screen and wait.
   *
   * Covers both halves of that wait: the canned-response rebuild (one LLM call)
   * and the conversation restart that replays the scenario under the new
   * settings. Whole-surface rather than greeting-scoped, which is what an
   * earlier pass did: once the conversation replays, every reply on screen is
   * stale, not just the opening line.
   */
  settingsApplying?: boolean
  scenario: MockOrderScenario | null
  resolutionData: ResolutionData | null
  escalationData: EscalationData | null
  connectionError?: string | null
  /**
   * Optional read-only agent-settings summary (AI-95). When provided the engine
   * renders AgentSettingsSummary in the right column. The shells pass
   * useSimulatorData().config; the legacy in-tab sandbox omits it.
   */
  config?: Pick<
    RecoveryConfig,
    'brand_voice_mode' | 'philosophy_posture' | 'approved_reward_ids'
  >
  /**
   * AI-202: grantable-reward count for AgentSettingsSummary, forwarded down so the
   * count derives from the shell's single useSimulatorData().approvedRewards source
   * rather than a second in-component fetch/filter. The shells pass
   * approvedRewards.length; index.tsx (which renders no summary) omits it.
   */
  rewardCount?: number
  /**
   * Optional approved-reward previews (AI-164). When provided the engine renders
   * a SandboxRewardCard per reward in the right column — a visual of exactly what
   * the agent can grant. Optional so the wizard call site (WizardSimulator),
   * which does not pass it, keeps type-checking (FE-2 seam).
   */
  approvedRewards?: AvailableReward[]
  /**
   * AI-585: whether to render the right-column SandboxRewardCard list. Defaults
   * true. Decoupled from `approvedRewards` so the wizard can forward the array
   * (enriching the inline offer image via reward_id match) while keeping the
   * right-column list hidden (WizardSimulator passes false).
   */
  showRewardPreviews?: boolean
  /**
   * Whether to render the read-only AgentSettingsSummary in the right column.
   * Defaults true (the wizard-embedded shell shows it). The agent_tuning
   * StandaloneSimulator passes false because the on-tab AgentTuningConfigModals
   * card already shows (and lets the merchant edit) those same values — avoiding
   * a duplicate readout (AI-167 / DS-3 Option B).
   */
  showAgentSettings?: boolean
  /**
   * AI-204: whether to render the dev-only SandboxPromptAccordion. Defaults true
   * (in-tab / standalone). The wizard variant passes false — the Figma wizard
   * layout has no sandbox-prompt pane.
   */
  showSandboxPrompt?: boolean
  /**
   * AI-204: 'wizard' retitles the DecisionLogPanel "AI reasoning" to match the
   * onboarding Figma. Defaults 'default' (in-tab / standalone keep "Decision Log").
   */
  decisionLogVariant?: 'default' | 'wizard'
  /**
   * AI-204: 'wizard' restyles the AgentSettingsSummary card (subtitle, "Recovery
   * generosity" label, stacked rows). Defaults 'default' (standalone unaffected).
   */
  agentSettingsVariant?: 'default' | 'wizard'
  /**
   * Chat presentation (AI-162). 'phone' wraps the conversation in a PhoneFrame
   * device mockup (the wizard's "what the customer sees" preview); 'panel'
   * (default) keeps the flat card used by the in-tab + standalone shells.
   */
  chatVariant?: 'panel' | 'phone'
  /**
   * ENG-971: the scenario title rendered above the phone (e.g. "VIP customer
   * missing an item"). The wizard supplies one in BOTH phases — the persona name
   * while guided, "Test your own" once free. Optional only for the standalone
   * shell; supplying nothing in the wizard would unmount the header and move the
   * phone.
   */
  scenarioHeading?: string
  /**
   * Single named layout slot: renders at the top of the right column INSTEAD OF
   * the read-only AgentSettingsSummary, so both shells mount the one interactive
   * card. Deliberate exception to "no JSX-tree props" — the card fetches its own
   * data, so the engine can only place it and stay a pure presenter.
   *
   * Opt out with `undefined`, never `false`: `??` keeps `false` and would then
   * render neither the card nor the summary.
   */
  settingsCard?: React.ReactNode
  /**
   * Intended for the wizard's guided phase to supply a hard-coded
   * per-scenario reasoning checklist, which would render INSTEAD OF the
   * DecisionLogPanel — the two are designed never to stack, so
   * `data-tour="reasoning"` would have exactly ONE carrier by construction
   * (DecisionLogPanel.tsx:110 takes that anchor in the wizard variant, and
   * WizardTour resolves it with a first-match querySelector). The free phase
   * and the Agent Tuning tab would pass nothing and keep the live decision log.
   *
   * ⚠ Currently NO production caller passes this prop — WizardSimulator
   * mounts SimulatorEngine without `reasoningChecklist`, so the wizard's
   * guided phase renders the live DecisionLogPanel today, and
   * SCENARIO_REASONING (scenarioReasoning.ts) is exercised only by its own
   * unit tests and this component's `reasoningChecklist`-specific test file.
   * Re-wiring this prop for real is exactly the moment to re-verify
   * SCENARIO_REASONING's copy is current (see the comment on that constant) —
   * it was last refreshed for the personaScenarios.ts premises as of PR #4014,
   * not for whatever premises exist when this gets re-wired.
   *
   * Opt out with `undefined`, never an empty array you did not mean: the swap
   * gates on truthiness, and `[]` would render an anchor-carrying blank card.
   */
  reasoningChecklist?: ScenarioReasoningStep[]
  /** Opens an agent-tuning config row from a checklist "Tweak …" link. */
  onOpenConfigRow?: (key: RowKey) => void
  /**
   * ENG-971: the small pager chip above the title ("Scenario 1 of 3"), rendered
   * uppercase in a tinted capsule. Omitted when there is nothing to page through.
   */
  scenarioChip?: string
  /**
   * ENG-978: when provided, a "Reset conversation" text link renders at the top
   * of the center column, above the phone. Pass it for a whole phase rather than
   * only when a reset would do something — the wizard's free phase pairs it with
   * the "Test your own" title to match the guided phase's two header rows, and
   * toggling it mid-phase moves the phone. Use `resetDisabled` to stand it down.
   */
  onReset?: () => void
  /**
   * Stands the reset link down without unmounting it, so the row above the phone
   * keeps its height. The shell passes true while a turn is in flight: resetting
   * mid-reply leaks that turn's inbound into the next conversation. Separate from
   * `onReset` on purpose — omitting the handler would remove the row and move the
   * phone, which is the bug this pair exists to avoid.
   */
  resetDisabled?: boolean
  /**
   * Scripted demo mode (R1-R4) — set only by the wizard's guided phase. Locks
   * the phone, pins its height and dresses the in-chat reward card as the
   * consumer app. Three separate props rather than one object: a
   * `scriptedDemo?: {…}` whose PRESENCE meant "scripted" would repeat the
   * coercion footgun this file already documents on `settingsCard`.
   */
  scriptedDemo?: boolean
  /**
   * Live-canned guided mode (the wizard's replacement for the scripted demo):
   * the socket is real and the agent's replies are genuine, but the CUSTOMER
   * turns are sent by the shell, so the merchant watches. Forwarded to
   * ChatPanel, which greys the compose box rather than dropping it and hides the
   * quick-reply chips.
   */
  composeLocked?: boolean
  /**
   * Keeps ChatPanel's opening greeting on screen once the log fills — the guided
   * phase's first canned turn arrives within seconds and would otherwise take it
   * away.
   */
  pinGreeting?: boolean
  /**
   * The canned in-chat reward card. Supplied outright because
   * offerFromResolution synthesizes a THIN AvailableReward when no
   * approved-reward template matches, dropping the points/value_cents/image_url
   * the canonical card needs.
   */
  scriptedOffer?: AvailableReward | null
  /**
   * Anchors that card mid-conversation (canonical invariant #4) instead of at
   * the tail of the log.
   */
  offerAfterMessageId?: string
  /**
   * Center-column layout (AI-162 wizard). 'wizard' drops the left
   * ScenarioDetailPanel — the header above the phone replaces the detail card —
   * and keeps the wizard's '360px 1fr' grid. Defaults 'default' (in-tab /
   * standalone). Kept separate from `scenarioHeading` so the wizard's free phase
   * can drop its heading (ENG-978) without silently changing the grid.
   */
  layoutVariant?: 'default' | 'wizard'
}

const SimulatorEngine = ({
  messages,
  currentStreamingMessage,
  awaitingResponse,
  isToolRunning,
  onSendMessage,
  assistantName,
  agentName,
  cannedGreeting,
  settingsApplying = false,
  scenario,
  resolutionData,
  escalationData,
  connectionError = null,
  config,
  rewardCount,
  approvedRewards,
  showRewardPreviews = true,
  showAgentSettings = true,
  showSandboxPrompt = true,
  decisionLogVariant = 'default',
  agentSettingsVariant = 'default',
  chatVariant = 'panel',
  scenarioHeading,
  settingsCard,
  reasoningChecklist,
  onOpenConfigRow,
  scenarioChip,
  onReset,
  resetDisabled = false,
  scriptedDemo,
  composeLocked,
  pinGreeting,
  scriptedOffer,
  offerAfterMessageId,
  layoutVariant = 'default',
}: Props) => {
  const [css] = useStyletron()
  // The phone's chrome is the MERCHANT's, not Thanx's (D4, approved 2026-08-17): the
  // device previews what the customer sees, and ChatPanel's greeting already names the
  // merchant, so a "RecoveryAI" header contradicted the copy directly beneath it.
  // `assistant_name` stays behind it as the fallback for a merchant record that has not
  // resolved yet.
  //   context: a useSelector read, not a fetch — this engine still owns no data
  //   fetching and no dispatch. ChatPanel and ChatInput already read the same hook for
  //   the greeting and the send button (ENG-977/979); this is the third consumer.
  const { name: merchantName, logoUrl: merchantLogoUrl } = useMerchantBrand()
  const phoneBrandName =
    merchantName || assistantName || t('default_assistant_name')

  // ONE terminal outcome for the whole engine, so the children cannot disagree.
  // Both payloads can be non-null at once — useRecoveryChat sets each in its own
  // case and only reset() clears them, and thanx-ai assigns them independently
  // across a turn.
  //
  //   context: ESCALATION wins. ChatPanel locks its compose box on an escalation,
  //   so letting the resolution through would put a granted-reward card (and an
  //   inline offer) beside a chat that says the case went to a human — a lie in
  //   the reassuring direction, which is the worst one to ship.
  const terminalOutcome = resolveTerminalOutcome(resolutionData, escalationData)
  const activeResolution =
    terminalOutcome === 'resolution' ? resolutionData : null
  const activeEscalation =
    terminalOutcome === 'escalation' ? escalationData : null

  // Inline chat-offer (AI-516; rendered directly by ChatPanel per AI-584):
  // derive the reward the agent DID grant from the terminal resolution,
  // preferring the enriched approvedRewards template. Threaded into ChatPanel so
  // it surfaces in-conversation, alongside the right-column preview + Decision Log.
  // A scripted demo supplies its card outright and withholds it until the
  // transcript reaches its anchor, so it bypasses the resolution derivation.
  const offer = useMemo(
    () =>
      scriptedDemo
        ? scriptedOffer ?? null
        : offerFromResolution(activeResolution, approvedRewards),
    [scriptedDemo, scriptedOffer, activeResolution, approvedRewards]
  )

  const isPhone = chatVariant === 'phone'
  const isWizardLayout = layoutVariant === 'wizard'
  // The header above the phone REPLACES the detail panel (AI-162): drop the left
  // ScenarioDetail column in the wizard layout, even though a scenario is present.
  const showScenarioDetail = scenario != null && !isWizardLayout
  // The right rail's order-visibility card is the EXACT complement of the left
  // ScenarioDetailPanel, so the order is always shown exactly once, never twice
  // and never dropped:
  //   - wizard layout (guided OR free "Test your own"): ScenarioDetailPanel is
  //     never shown (the centered header replaces it), so this card always is.
  //   - non-wizard layout with no scenario (free chat / Agent Tuning): neither
  //     panel has anything from ScenarioDetailPanel to show, so this card is
  //     the only order-visibility surface.
  //   - non-wizard layout WITH a scenario: ScenarioDetailPanel already shows
  //     the order, so this card stays hidden rather than duplicating it.
  const showOrderDetailsCard = !showScenarioDetail
  // The order the AI is actually reasoning over (mirrors useSimulatorData's
  // `order_context: scenario?.order ?? getFreeChatOrder()` exactly, down to
  // the same day-cached resolver) — so whenever this card renders, it can
  // never show an order other than the one in the live conversation context.
  const orderDetailsOrder: MockOrder = scenario?.order ?? getFreeChatOrder()
  // ENG-971 / ENG-978: the center column grows a header (reset link, pager chip,
  // scenario title) whenever the shell supplies any of those parts.
  const showCenterHeader =
    scenarioHeading !== undefined ||
    scenarioChip !== undefined ||
    onReset !== undefined
  // EVERY phone surface is now the balanced two-equal-column grid (D1, approved
  // 2026-08-17): the wizard and the Agent Tuning tab are meant to mirror each other,
  // so the wizard's old fixed 360px phone track is gone rather than forked.
  const gridColumns = showScenarioDetail
    ? '280px 1fr 300px'
    : isPhone
    ? 'minmax(0, 1fr) minmax(0, 1fr)'
    : '1fr 300px'

  const chatEl = (
    <ChatPanel
      messages={messages}
      currentStreamingMessage={currentStreamingMessage}
      awaitingResponse={awaitingResponse}
      isToolRunning={isToolRunning}
      onSendMessage={onSendMessage}
      assistantName={assistantName}
      agentName={agentName}
      cannedGreeting={cannedGreeting}
      bare={isPhone}
      offer={offer}
      // Escalation is the other terminal state: the agent routed to the team and
      // stopped listening, so ChatPanel locks the compose box (same as a granted
      // reward). The escalation message itself rides the normal `messages` stream.
      // Reads the shared outcome, not `escalationData` directly, so this can never
      // disagree with what the reasoning card narrates.
      escalated={terminalOutcome === 'escalation'}
      scriptedDemo={scriptedDemo}
      composeLocked={composeLocked}
      pinGreeting={pinGreeting}
      offerAfterMessageId={offerAfterMessageId}
    />
  )

  // What the phone shows instead of the conversation while a save is landing.
  // REPLACES the conversation rather than dimming it, which is what an earlier
  // pass did: a save invalidates every reply on screen, so a greyed-out copy of a
  // conversation that is about to be discarded is exactly the confusing
  // intermediate state this exists to remove.
  //
  //   context: this is also what makes the surface inert, with no
  //   `pointerEvents: none` anywhere — the compose box, the quick-reply chips and
  //   the greeting are unmounted for the duration, so there is nothing left for a
  //   click to land on. Disabling in place would have left all three mounted over
  //   a conversation about to be thrown away.
  //   context: NOT a bare blank screen, which was the first shape of this ask. A
  //   dead device for the ~13s a canned rebuild takes reads as broken; the
  //   spinner and the caption are what make the wait legible.
  //   context: rendered INSIDE the device (and inside the panel's own box), never
  //   above it. Anything above the phone adds a layout row and moves the device —
  //   the one thing every height decision in PhoneFrame exists to prevent, and
  //   what would re-open the coach tour's spotlight re-measure.
  //   context: role="status" is polite on purpose. This is progress on something
  //   the merchant just asked for, not an alert. The visible caption is the
  //   region's accessible name, so it needs no separate sr-only text.
  const applyingEl = (
    <div
      role="status"
      className={css({
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        height: '100%',
        justifyContent: 'center',
        minHeight: isPhone ? '0' : `${APPLYING_PANEL_MIN_HEIGHT_PX}px`,
        padding: '0 24px',
        textAlign: 'center',
      })}
    >
      <Spinner isLoading size="3x" />
      <Text.BodyText4 color="grey70">{t('settings_applying')}</Text.BodyText4>
    </div>
  )

  // Swapped on the shared chat body rather than inside PhoneFrame, so the flat
  // 'panel' presentation (Agent Tuning) runs the same code and PhoneFrame stays
  // purely device chrome.
  const chatBody = settingsApplying ? applyingEl : chatEl

  const centerChat = isPhone ? (
    <PhoneFrame
      brandName={phoneBrandName}
      brandLogoUrl={merchantLogoUrl ?? undefined}
      dataTour={TOUR_ANCHORS.simulator}
      // PIN the device on every phone surface (AI-582 capped it; review widened
      // this to a pin). A cap alone leaves a 560-600px band whose resolved value
      // follows the grid row, so the device resized as the right column filled
      // in. Pinning holds a real phone shape and lets overflow-y:auto scroll the
      // conversation inside it, which is the behaviour design asked for.
      //   context: no maxHeightPx alongside it — buildPhoneHeightStyle returns
      //   early on fixedHeightPx, so a cap here could never apply. The pin value
      //   IS the old cap, so no surface changes height, only stops moving.
      fixedHeightPx={PHONE_FIXED_HEIGHT_PX}
    >
      {chatBody}
    </PhoneFrame>
  ) : (
    chatBody
  )

  const centerColumn = showCenterHeader ? (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        minWidth: 0,
      })}
    >
      <div
        data-tour={TOUR_ANCHORS.scenarioHeader}
        className={css({
          textAlign: 'center',
          // Two rows above the phone in BOTH phases, so its top never moves:
          // guided is chip + persona title, free is the reset link + "Test your
          // own". The reset link is rendered for the whole free phase rather than
          // only when there is something to reset, so its stack is two rows too.
          //   context: no minHeight reservation. An earlier pass floored this box
          //   at 80px, but with the rows balanced structurally that number was
          //   doing no work — and a magic pixel value that is not load-bearing is
          //   just debt. If the chip capsule and the link line-box ever resolve
          //   to meaningfully different heights, reserve it deliberately then.
          //   context: this box also carries the tour's scenarioHeader anchor, so
          //   keeping it mounted across the phase flip keeps that rect stable.
        })}
      >
        {/* ENG-978: reset moved out of the bottom button row to a text link at
            the top of the phone. `kind="minimal"` keeps it a real button (uikit
            a11y defaults) while reading as a link; see TEXT_LINK_OVERRIDES for
            why its override has to be complete. Free-phase only, and rendered
            for the WHOLE phase rather than once there is something to clear —
            appearing later moved the phone. `resetDisabled` still stands it down
            mid-turn, which is a correctness guard rather than a cosmetic one:
            resetting with a reply inbound leaks that turn into the next one. */}
        {onReset && (
          <div className={css(headerRowStyle)}>
            <Button
              disabled={resetDisabled}
              kind="minimal"
              onClick={onReset}
              overrides={TEXT_LINK_OVERRIDES}
            >
              <i
                className={`fa fa-refresh ${css({ marginRight: '6px' })}`}
                aria-hidden="true"
              />
              {t('reset_conversation')}
            </Button>
          </div>
        )}
        {/* ENG-971: the pager chip ("Scenario 1 of 3") sits above the scenario
            title in a tinted capsule. Uppercased in CSS so the underlying (and
            announced) text stays sentence case. */}
        {scenarioChip !== undefined && (
          <div className={css(headerRowStyle)}>
            <span
              className={css({
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: `${CHIP_RADIUS_PX}px`,
                backgroundColor: palette.spearmint10,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              })}
            >
              {/* grey90, not spearmint60 — and this is a FIX, not a preference.
                  spearmint60 (#229D9D) on spearmint10 is 3.03:1, which only satisfies
                  WCAG 1.4.3 for LARGE text (>=18px, or >=14px bold). BodyText5 is 12px,
                  so the chip was already short of the 4.5:1 it needs, and dropping it
                  to BodyText6 (10px, the Figma size) could not fix that — no teal in
                  the palette clears 4.5:1 on this wash (spearmint50 is 2.05:1). grey90
                  is 9.44:1 and the mint capsule still carries the chip's identity.
                    context: if design wants the teal text back, it needs a spearmint
                    darker than #1A7A7A as a new palette token — flag it rather than
                    reintroducing the failure. */}
              <Text.BodyText6 bold color="grey90">
                {scenarioChip}
              </Text.BodyText6>
            </span>
          </div>
        )}
        {/* BodyText4, not BodyText3 — the Figma scenario title is 14px bold. */}
        {scenarioHeading !== undefined && (
          <Text.BodyText4 bold>{scenarioHeading}</Text.BodyText4>
        )}
      </div>
      {centerChat}
    </div>
  ) : (
    centerChat
  )

  return (
    <div>
      {/* Surface ActionCable connection errors at the top of the sandbox
          so a `rejected()` (auth failure, channel missing) or a malformed
          payload from the backend is visible to the merchant instead of
          silently held in the hook's state. */}
      {connectionError && (
        <div
          role="alert"
          className={`mb-m ${css({
            padding: '12px 16px',
            borderRadius: '4px',
            border: `1px solid ${merchantTheme.colors.cayenne50}`,
            backgroundColor: merchantTheme.colors.cayenne10,
          })}`}
        >
          <Text.BodyText4 color="cayenne50">{connectionError}</Text.BodyText4>
        </div>
      )}

      {/* Scenario (left, only when a scenario is present AND no wizard heading)
          + Chat/phone (center) + Decision Log (right). Drop the left track when
          scenario is null (free-chat standalone) or a wizard heading replaces
          the detail card, so the chat doesn't collapse into 280px. */}
      <div
        className={css({
          display: 'grid',
          gridTemplateColumns: gridColumns,
          gap: `${isPhone ? PHONE_GRID_GAP_PX : DEFAULT_GRID_GAP_PX}px`,
          alignItems: 'stretch',
          minHeight: '600px',
          // Cap + center BOTH phone layouts (tab and wizard) so the columns read
          // as a balanced, centered pair instead of sprawling across / hugging the
          // left with a dead gap on the right.
          maxWidth: isPhone ? `${PHONE_GRID_MAX_WIDTH_PX}px` : undefined,
          margin: isPhone ? '0 auto' : undefined,
        })}
      >
        {/* Left: scenario details (non-wizard shells). The `scenario &&` re-narrows
            to non-null for the type checker (showScenarioDetail already implies it). */}
        {showScenarioDetail && scenario && (
          <ScenarioDetailPanel scenario={scenario} />
        )}

        {/* Center: chat (flat panel) or phone mockup, with optional heading */}
        {centerColumn}

        {/* Right: read-only agent settings (optional) + sandbox prompt editor
            stacked above decision log. Flex column at full row height so the
            decision log grows into the REMAINING space (flexGrow) and its bottom
            lines up with the scenario/chat panes — rather than each card sizing
            to content and the log overflowing the column. */}
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            height: '100%',
          })}
        >
          {/* The interactive card (when a shell supplies one) REPLACES the
              read-only summary — `??` falls back only when no node is passed, so
              the two never stack. */}
          {settingsCard ??
            (showAgentSettings && config ? (
              <AgentSettingsSummary
                config={config}
                variant={agentSettingsVariant}
                rewardCount={rewardCount}
              />
            ) : null)}
          {/* Order details (see showOrderDetailsCard for when this vs. the left
              ScenarioDetailPanel renders), sitting between the agent settings
              card and the AI reasoning / reasoning checklist card below. */}
          {showOrderDetailsCard && (
            <OrderDetailsCard order={orderDetailsOrder} />
          )}
          {/* Rich preview of exactly what the agent can grant (AI-164): one
              SandboxRewardCard per approved reward. Gated on showRewardPreviews
              (AI-585) so the wizard can forward approvedRewards for inline-offer
              image matching while keeping this list hidden. The error/loading
              gate lives upstream in the shell (StandaloneSimulator shows
              SimulatorStatus and never mounts the engine on error/loading — D2),
              so the engine stays a pure, data-fetch-free presenter. */}
          {showRewardPreviews && approvedRewards && approvedRewards.length > 0 && (
            <div
              className={css({
                display: 'grid',
                // Thin reward cards flow into as many ~180px columns as fit
                // rather than each stretching full-width with trailing whitespace.
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: '8px',
              })}
            >
              {approvedRewards.map(reward => (
                <SandboxRewardCard key={reward.id} reward={reward} />
              ))}
            </div>
          )}
          {showSandboxPrompt && <SandboxPromptAccordion />}
          {reasoningChecklist && config ? (
            <ReasoningChecklist
              steps={reasoningChecklist}
              // `config` is what makes philosophy_posture available, and
              // RecoveryConfig types it non-optional — so gating the swap on
              // `config` removes the undefined-posture path entirely rather
              // than papering over it with fallback copy (D12).
              posture={config.philosophy_posture}
              onOpenConfigRow={onOpenConfigRow}
            />
          ) : (
            <DecisionLogPanel
              resolutionData={activeResolution}
              escalationData={activeEscalation}
              variant={decisionLogVariant}
              // Powers the wizard variant's live narrative: `config` names the
              // brand-voice source, `approvedRewards` lets the make-good step
              // match on reward_id rather than assert provenance blind. Forwarded
              // even when showRewardPreviews is false (AI-585).
              config={config}
              approvedRewards={approvedRewards}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default SimulatorEngine
